import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";
import {
  FaqArticleDbParser_,
  FaqCollectionDbParser_,
  FaqPagesDbParser_,
} from "./faq-db-parser";

// Action button types
export const faqActionButtonType = [
  "track_package",
  "start_return",
  "contact_us",
] as const;
export type FaqActionButtonType = (typeof faqActionButtonType)[number];

export enum FaqActionButtonTypeEnum {
  TRACK_PACKAGE = "track_package",
  START_RETURN = "start_return",
  CONTACT_US = "contact_us",
}

export const FaqActionButtonSchema = z.object({
  type: z.enum(faqActionButtonType),
  enabled: z.boolean(),
});
export type FaqActionButton = z.infer<typeof FaqActionButtonSchema>;

const faqThemeBodySchema_ = z.object({
  brandKitOverrides: z.object({ pageBackgroundColor: z.boolean() }),
  homeBackgroundImageUrl: z.string().nullish(),
  overlayColor: z.string(),
  overlayOpacity: z.number(),
  headlineText: z.string(),
  headlineTextColor: z.string(),
  pageBackgroundColor: z.string(),
  displayCollectionIcon: z.boolean(),
  displayCollectionSubtext: z.boolean(),
  collectionBackgroundColor: z.string(),
  collectionBorderColor: z.string(),
  collectionCornerRadius: z.number(),
  headingSectionPaddingLeft: z.number().nullish(),
  headingSectionPaddingRight: z.number().nullish(),
  headingSectionPaddingBottom: z.number().nullish(),
  headingSectionPaddingTop: z.number().nullish(),
  headingSectionPaddingBetweenElements: z.number().nullish(),
  navigationTitle: z.string().nullish(),
  navigationDescription: z.string().nullish(),
  selectedItemOverlayColor: z.string().nullish(),
  showArticleCount: z.boolean().nullish(),
  showArticleCountInSideBar: z.boolean().nullish(),
  showArticleDescription: z.boolean().nullish(),
  actionButtons: z.array(FaqActionButtonSchema).nullish(),
});
export const faqThemeBodySchema: z.ZodType<IFaqTheme> = faqThemeBodySchema_;
export interface IFaqTheme extends z.infer<typeof faqThemeBodySchema_> {}

export function defaultFaqTheme(): IFaqTheme {
  return {
    brandKitOverrides: { pageBackgroundColor: false },
    overlayColor: "#ffffff",
    overlayOpacity: 0,
    headlineText: "Help center",
    headlineTextColor: "#141414",
    pageBackgroundColor: "#fafafa",
    displayCollectionIcon: false,
    displayCollectionSubtext: true,
    collectionBackgroundColor: "#ffffff",
    collectionBorderColor: "#d6d6d6",
    collectionCornerRadius: 8,
    headingSectionPaddingLeft: 416,
    headingSectionPaddingRight: 416,
    headingSectionPaddingBottom: 124,
    headingSectionPaddingTop: 124,
    headingSectionPaddingBetweenElements: 16,
    navigationTitle: "Categories",
    navigationDescription: "Browse our frequently asked questions by category",
    selectedItemOverlayColor: "#e5e5e5",
    showArticleCount: true,
    showArticleCountInSideBar: false,
    showArticleDescription: true,
    actionButtons: [
      { type: "track_package", enabled: true },
      { type: "start_return", enabled: true },
      { type: "contact_us", enabled: true },
    ],
  };
}

const faqCollectionBodySchema_ = FaqCollectionDbParser_.extend({
  _id: z.string(),
  team: z.string(),
  articles: z.array(z.string()),
});
export const faqCollectionBodySchema: z.ZodType<FaqCollection> =
  faqCollectionBodySchema_;
export interface FaqCollection
  extends z.infer<typeof faqCollectionBodySchema_> {}

// Frontend schema without embeddings (shared across boundaries)
export const faqArticleBodySchema_ = FaqArticleDbParser_.omit({
  embeddings: true,
}).extend({
  _id: z.string(),
  team: z.string(),
  faqCollection: z.string(),
  whenToUse: z.string().optional(),
});
export const faqArticleBodySchema: z.ZodType<FaqArticle> =
  faqArticleBodySchema_;
export interface FaqArticle extends z.infer<typeof faqArticleBodySchema_> {}

const faqPagesBodySchema_ = FaqPagesDbParser_.extend({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  title: z.string().optional().default("Help Center"),
  description: z.string().optional(),
  collections: z.array(zExt.objectId()),
});
export const faqPagesBodySchema: z.ZodType<FaqPages> = faqPagesBodySchema_;
export interface FaqPages extends z.infer<typeof faqPagesBodySchema_> {}
